import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ContactModal({ open, onClose, user }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // Prefill from the signed-in account each time the modal opens.
  useEffect(() => {
    if (open) {
      setSent(false);
      setBusy(false);
      const uEmail = user?.email || "";
      const isSystem = /\.local$/i.test(uEmail);
      // Strip parentheticals like "(demo)" and skip system/admin placeholder accounts.
      const raw = (user?.name || "").replace(/\s*\(.*?\)\s*/g, " ").trim();
      const parts = raw.split(/\s+/).filter(Boolean);
      const looksAdmin = isSystem || /^admin$/i.test(raw);
      setFirstName(looksAdmin ? "" : (parts[0] || ""));
      setLastName(looksAdmin ? "" : (parts.slice(1).join(" ") || ""));
      // Skip prefilling internal/system accounts (e.g. admin@stepmystep.local) — show the placeholder instead.
      setEmail(isSystem ? "" : uEmail);
    }
  }, [open, user]);

  if (!open) return null;

  const valid = firstName.trim() && email.trim() && message.trim();
  const av = user?.avatar;

  const submit = async () => {
    if (!valid) {
      toast.error("Please add your first name, email, and a message");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSent(true);
      setSubject("");
      setMessage("");
      setTimeout(() => { setSent(false); onClose(); }, 1900);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't send — please check your email and try again");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      style={{ background: "rgba(23,20,38,.62)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="contact-modal"
    >
      <div
        className="contact-dark w-full sm:max-w-md rounded-t-3xl sm:rounded-[24px] relative my-auto max-h-[94vh] flex flex-col overflow-hidden"
        style={{ background: "#0f0f16", border: "2px solid #5d8a52", boxShadow: "0 30px 80px -24px rgba(0,0,0,.75), 0 0 44px -10px rgba(93,138,82,.4)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="contact-close"
          onClick={onClose}
          className="absolute top-3.5 right-3.5 p-2 rounded-full text-white/80 hover:bg-white/15 transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {sent ? (
          <div className="px-6 py-14 text-center" data-testid="contact-thankyou">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center border-2 border-[#5d8a52]" style={{ background: "#14261a" }}>
              <Mail size={26} className="text-[#8fbf82]" />
            </div>
            <h2 className="text-xl font-extrabold mt-4 text-white">Thank you for reaching out</h2>
            <p className="text-sm text-white/60 mt-1">We read every message. 💚</p>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 text-white shrink-0" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Mail size={20} /></span>
                <div>
                  <h2 className="text-lg font-extrabold leading-tight">Contact us</h2>
                  <p className="text-[13px] font-semibold text-white/85 leading-tight">We'd love to hear from you</p>
                </div>
              </div>
            </div>

            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_workflow-clone-29/artifacts/ckvvv2mr_StepMyStep%20Offices.png"
              alt="Vittyyo offices — where we're headed"
              className="w-full h-28 sm:h-32 object-cover object-center shrink-0"
              data-testid="contact-office-banner"
            />

            <div className="px-6 pt-3 pb-3 space-y-2 overflow-y-auto">
              <p className="text-[13px] text-white/80 leading-snug rounded-2xl p-3" style={{ background: "#0f1a0c", border: "1px solid #5d8a52" }}>
                Welcome to Vittyyo — we hope you're enjoying our community. If there's anything you'd like to share — ideas, feedback, or something to flag — please let us know. 💚
              </p>

              {av && (av.emoji || av.tag) && (
                <div className="flex items-center gap-2 text-xs font-semibold text-white/60" data-testid="contact-avatar-stamp">
                  <span className="flex items-center gap-0.5 bg-black border border-[#5d8a52] rounded-full px-2.5 py-1 shrink-0">
                    <span className="text-base leading-none">{av.emoji}</span><span className="font-extrabold text-[#8fbf82]">{av.tag}</span>
                  </span>
                  <span>Your avatar is stamped on this message so we know it's really you.</span>
                </div>
              )}

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs font-bold text-white/70">First name</label>
                  <input data-testid="contact-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Your Name" className="sms-input w-full px-4 py-2 mt-1" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold text-white/70">Last name <span className="font-normal text-white/40">(optional)</span></label>
                  <input data-testid="contact-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" className="sms-input w-full px-4 py-2 mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-white/70">Email</label>
                <input data-testid="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your Email Here" className="sms-input w-full px-4 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold text-white/70">Subject</label>
                <input data-testid="contact-subject" value={subject} maxLength={140} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?" className="sms-input w-full px-4 py-2 mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold text-white/70">Message</label>
                <textarea data-testid="contact-message" value={message} maxLength={4000} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Tell us what's on your mind…" className="sms-input w-full px-4 py-2 mt-1 resize-none" />
              </div>
            </div>

            <div className="px-6 py-3 border-t border-white/10 shrink-0">
              <button
                data-testid="contact-send"
                onClick={submit}
                disabled={busy || !valid}
                className="sms-solid-btn w-full text-white font-bold py-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
